import {copyText, el, marketingTag, prefersReducedMotion} from "./progress"

const LABELS = {spf: "SPF", dkim: "DKIM", dmarc: "DMARC", mx: "MX"}
const ORDER = ["spf", "dkim", "dmarc", "mx"]
const SEVERITY = {bad: "text-bad", warn: "text-warn", info: "text-info"}

function statusTag(entry) {
  const status = entry?.status
  if (status === "pass") return marketingTag("Pass", "green", true)
  if (status === "warn") return marketingTag("Check", "yellow", true)
  const hasValue = Boolean(entry?.record) || Boolean(entry?.hosts?.length) || Boolean(entry?.selectors?.length)
  return marketingTag(hasValue ? "Fail" : "Missing", "red", true)
}

function recordText(kind, entry) {
  if (!entry) return ""
  if (kind === "dkim") return (entry.selectors || []).join(", ")
  if (kind === "mx") {
    return (entry.hosts || []).map((row) => `${row.priority} ${row.host}`).join(", ")
  }
  return entry.record || ""
}

function checkedLabel(iso) {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (!Number.isFinite(minutes) || minutes < 1) return "Checked just now"
  return minutes === 1 ? "Checked 1 minute ago" : `Checked ${minutes} minutes ago`
}

function animateGrade(node) {
  if (prefersReducedMotion()) return
  node.style.transform = "scale(0.84)"
  node.style.opacity = "0"
  node.style.transition = "transform 320ms ease-out, opacity 320ms ease-out"
  window.requestAnimationFrame(() => {
    node.style.transform = "scale(1)"
    node.style.opacity = "1"
  })
}

function showMessage(result, text, tone) {
  const note = el("p", tone === "bad" ? "text-[14px] text-bad" : "text-[14px] text-ink-muted", text)
  result.replaceChildren(note)
}

const REACTIONS = {A: "delighted", B: "sparkle", C: "surprised", D: "bashful", F: "dizzy"}

function shiba(root, grade) {
  const {mascotDirections, mascotReactions} = root.dataset
  if (!mascotDirections || !mascotReactions || !window.__pageMascotMount) return null
  const button = document.createElement("button")
  button.type = "button"
  button.className = "shrink-0"
  button.setAttribute("aria-label", "PostShiba")
  Object.assign(button.style, {display: "inline-block", width: "64px", height: "64px", padding: "0", border: "none", background: "transparent", cursor: "pointer"})
  Object.assign(button.dataset, {pageMascot: "", directions: mascotDirections, reactions: mascotReactions, reaction: REACTIONS[grade] || "surprised"})
  return button
}

function renderReport(root, result, data) {
  const card = el("div", "overflow-hidden rounded-xl bg-surface-overlay shadow-mock")
  const head = el("div", "flex flex-wrap items-end justify-between gap-4 border-b border-line px-4 py-4 sm:px-5")
  const lead = el("div", "flex items-end gap-4")
  const grade = el("p", "text-display leading-none text-ink tabular-nums", data.grade || "")
  const score = el("div", "min-w-0 pb-1")
  score.append(el("p", "text-[15px] font-medium text-ink tabular-nums", `${data.score ?? 0} of 100`))
  if (data.checked_at) score.append(el("p", "mt-1 text-[12px] text-ink-subtle", checkedLabel(data.checked_at)))
  lead.append(grade, score)
  head.append(lead)
  const mascot = shiba(root, data.grade)
  if (mascot) head.append(mascot)
  card.append(head)

  const list = el("div", "divide-y divide-line")
  ORDER.forEach((kind) => {
    const entry = (data.records && data.records[kind]) || {}
    const row = el("div", "grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:px-5")
    const label = el("div", "flex items-center gap-2")
    label.append(el("span", "text-[13px] font-medium text-ink", LABELS[kind]), statusTag(entry))
    const body = el("div", "min-w-0")
    const value = recordText(kind, entry)
    if (value) body.append(el("p", "break-all font-mono text-[12px] leading-5 text-ink", value))
    if (entry.summary) body.append(el("p", "mt-1 text-[13px] leading-5 text-ink-muted", entry.summary))
    row.append(label, body)
    list.append(row)
  })
  card.append(list)

  const anchorBase = root.dataset.anchorBase || ""
  const findings = Array.isArray(data.findings) ? data.findings : []
  if (findings.length) {
    const wrap = el("div", "border-t border-line px-4 py-3 sm:px-5")
    const items = el("ul", "space-y-2")
    findings.forEach((finding) => {
      const item = el("li", `text-[13px] leading-5 ${SEVERITY[finding.severity] || "text-ink-muted"}`)
      if (finding.anchor) {
        const link = el("a", "underline decoration-line-strong underline-offset-4 hover:decoration-ink", finding.message || "")
        link.href = `${anchorBase}#${finding.anchor}`
        item.append(link)
      } else {
        item.textContent = finding.message || ""
      }
      items.append(item)
    })
    wrap.append(items)
    card.append(wrap)
  }

  result.replaceChildren(card)
  if (mascot) window.__pageMascotMount(mascot)
  animateGrade(grade)
}

async function check(root, domain) {
  const result = root.querySelector("[data-learn-result]")
  const submit = root.querySelector("[type=submit]")
  const url = new URL(root.dataset.url || "", window.location.origin)
  url.searchParams.set("domain", domain)
  result.setAttribute("aria-busy", "true")
  if (submit) submit.disabled = true
  showMessage(result, "Checking the domain.")
  try {
    const response = await fetch(url.toString(), {headers: {Accept: "application/json"}})
    if (response.status === 429) {
      showMessage(result, "Too many checks. Try again in a minute.", "bad")
      return
    }
    const data = await response.json().catch(() => ({}))
    if (response.status === 422) {
      showMessage(result, data.message || data.error || "That domain could not be checked.", "bad")
      return
    }
    if (!response.ok) {
      showMessage(result, "The checker could not finish. Try again.", "bad")
      return
    }
    renderReport(root, result, data)
    const next = new URL(window.location.href)
    next.searchParams.set("domain", domain)
    window.history.replaceState({}, "", next)
  } catch (_error) {
    showMessage(result, "Could not reach the checker. Try again.", "bad")
  } finally {
    result.removeAttribute("aria-busy")
    if (submit) submit.disabled = false
  }
}

export function mount(root) {
  const form = root.querySelector("form")
  const input = root.querySelector("[data-learn-domain]")
  const live = root.querySelector("[data-learn-copy-status]")
  const copyLink = root.querySelector("[data-learn-copy-link]")
  form?.addEventListener("submit", (event) => {
    event.preventDefault()
    const domain = input?.value.trim()
    if (!domain) {
      showMessage(root.querySelector("[data-learn-result]"), "Enter a domain such as example.com.", "bad")
      return
    }
    check(root, domain)
  })
  copyLink?.addEventListener("click", () => {
    const href = new URL(root.dataset.shareUrl || window.location.href, window.location.origin)
    const domain = input?.value.trim()
    if (domain) href.searchParams.set("domain", domain)
    copyText(href.toString(), live, "Link copied.")
  })
  const preset = new URLSearchParams(window.location.search).get("domain")
  if (preset && input) {
    input.value = preset
    check(root, preset)
  }
}
